import PropTypes from 'prop-types';
import { React, useState, classNames, useEffect } from '@digsup/core-ui';
import debounce from 'lodash.debounce';
import { Search } from '@digsup/components-input';
import { geocodeByAddress } from 'react-places-autocomplete';
import SmartyStreetsSDK from 'smartystreets-javascript-sdk';
import { FetchSubUnits, FormatAddress } from './utils';
import { styles } from './AddressSearchBar.scss';

const cx = classNames.bind(styles);

export const AddressSearchBar = ({
  value,
  onSelect,
  className,
  smartyStreetsApiKey,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [searchValue, setSearchValue] = useState(value);
  const [, setSearchQuery] = useState({});

  const SmartyStreetsCore = SmartyStreetsSDK.core;
  const AutoCompleteLookup = SmartyStreetsSDK.usAutocompletePro.Lookup;

  const smartyStreetsSharedCredentials =
    new SmartyStreetsCore.SharedCredentials(smartyStreetsApiKey);

  const autoCompleteClientBuilder = new SmartyStreetsCore.ClientBuilder(
    smartyStreetsSharedCredentials,
  );

  const autoCompleteApi =
    autoCompleteClientBuilder.buildUsAutocompleteProClient();

  const classes = cx('c-map-address-searchbar', className);

  useEffect(() => {
    if (value) {
      setSearchValue(value);
    }
    return function cleanup() {
      setSearchValue('');
    };
  }, [value]);

  const autoCompleteLookUp = ({ searchText, selected, moreEntries }) => {
    const lookup = new AutoCompleteLookup(searchText);
    if (selected) {
      lookup.selected = moreEntries;
    }
    autoCompleteApi
      .send(lookup)
      .then(results => setSuggestions(results.result))
      .catch(error => {
        setSuggestions([]);
        console.error(error, 'Failed to retrieve Smarty Streets Address');
      });
  };

  const searchGoogleByAddress = (address, addressDetails) => {
    const details = { ...addressDetails };
    geocodeByAddress(address)
      .then(googleResult => {
        const result = googleResult[0];
        const pId = result?.place_id;
        const googleAddress = result?.formatted_address;
        setSearchValue(googleAddress);
        details.formattedAddress = googleAddress;
        onSelect(pId, details);
        setSuggestions([]);
      })
      .catch(console.error);
  };

  // Smarty Streets US Street API
  // gets property meta data
  const onSelectAddress = selectedAddress => {
    let unit;
    let unitType;
    const addressDetails = {};
    const address = FormatAddress({ address: selectedAddress });
    if (selectedAddress?.secondary) {
      unitType = FetchSubUnits(selectedAddress?.secondary)?.[0];
      unit = selectedAddress?.secondary;
    }
    // set search text
    setSearchValue(address);
    // find unit type
    addressDetails.unitType = unitType || undefined;
    // find unit
    addressDetails.unit = unit
      ? unit.toLowerCase().replace(unitType, '').trim().toUpperCase()
      : undefined;
    // search for googlePlaceId
    searchGoogleByAddress(address, addressDetails);
  };

  const onSearchAddress = () => {
    let unit;
    let unitType;
    let address;
    const addressDetails = {};
    const lookup = new AutoCompleteLookup(searchValue);
    autoCompleteApi
      .send(lookup)
      .then(results => {
        const addressFromLookUp = results.result[0];
        if (addressFromLookUp) {
          address = FormatAddress({ address: addressFromLookUp });
          if (addressFromLookUp?.secondary) {
            unitType = FetchSubUnits(addressFromLookUp?.secondary)?.[0];
            unit = addressFromLookUp?.secondary;
          }
          // set search text
          setSearchValue(address);
          // find unit type
          addressDetails.unitType = unitType || undefined;
          // find unit
          addressDetails.unit = unit
            ? unit.toLowerCase().replace(unitType, '').trim().toUpperCase()
            : undefined;
          // search for googlePlaceId
          searchGoogleByAddress(address, addressDetails);
        } else {
          searchGoogleByAddress(searchValue, addressDetails);
        }
      })
      .catch(error => {
        console.error(error, 'Failed to retrieve Smarty Streets Address');
      });
  };

  // Smarty Streets AutoComplete API
  // searches for child and parent buidling
  const onSearchSelect = selectedAddress => {
    const formattedAddress = FormatAddress({ address: selectedAddress });
    let moreEntries;
    // child building address listing for more than 1 Eg. APT 1 - 20
    if (selectedAddress.entries > 1) {
      moreEntries = FormatAddress({
        address: selectedAddress,
        moreEntries: true,
      });
    }
    autoCompleteLookUp({
      selected: selectedAddress.entries > 1,
      moreEntries,
      searchText: formattedAddress,
    });
  };

  // handles search bar on text change event on Map Component
  const handleOnChange = event => {
    const inputText = event?.target?.value;
    setSearchValue(inputText);

    const search = debounce(autoCompleteLookUp, 300);
    setSearchQuery(prevSearch => {
      if (prevSearch.cancel) {
        prevSearch.cancel();
      }
      return search;
    });
    search({ searchText: inputText });
  };

  return (
    <div className={classes}>
      <div className="c-map-address-searchbar__wrapper">
        <Search
          className={cx('c-map-address-searchbar__input', {
            'c-map-address-searchbar__input--has-suggestions':
              suggestions && suggestions.length > 0,
          })}
          enterButton="Find"
          placeholder={'Search...'}
          onSearch={() => onSearchAddress()}
          onChange={handleOnChange}
          hasError={false}
          value={searchValue}
          size="large"
        />
        {suggestions && suggestions.length > 0 && (
          <div className="c-map-address-searchbar__suggestions">
            {suggestions.map((suggestion, index) => {
              const formattedAddress = FormatAddress({ address: suggestion });
              let onSelectFunc = onSelectAddress;
              if (suggestion.entries > 1) {
                onSelectFunc = onSearchSelect;
              }
              return (
                <div
                  className="c-map-address-searchbar__suggestions-item"
                  key={index}
                  onClick={() => onSelectFunc(suggestion)}>
                  {formattedAddress}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

AddressSearchBar.propTypes = {
  onSelect: PropTypes.func,
  value: PropTypes.string,
  className: PropTypes.string,
  smartyStreetsApiKey: PropTypes.string.isRequired,
};

AddressSearchBar.defaultProps = {
  value: '',
  smartyStreetsApiKey: process.env.SMARTY_STREET_KEY,
};
